// Tiny shared helper for the "Send follow-up email after a live assist
// session" hop. The session-ended card writes a recap template into
// sessionStorage keyed by ticket id, then navigates to the ticket;
// ReplyComposer checks the key on mount and pre-fills the body.
//
// sessionStorage (not localStorage) on purpose: the prefill is a single-
// use intent tied to the current tab + this immediate navigation. If
// the agent reloads later we don't want last week's recap to reappear.

const PREFIX = 'pa-assist-followup:';

function key(ticketId: string): string {
  return PREFIX + ticketId;
}

/** Producer side — called by the session-ended card. */
export function stageAssistFollowup(ticketId: string, bodyHtml: string): void {
  try {
    sessionStorage.setItem(key(ticketId), bodyHtml);
  } catch {
    // private mode / quota — silent failure; the navigation still
    // happens, agent just composes from scratch.
  }
}

/** Consumer side — called by ReplyComposer on mount. Returns the
 *  staged HTML (if any) AND clears the key so re-mounts don't
 *  re-apply. Returns null if nothing's staged. */
export function consumeAssistFollowup(ticketId: string): string | null {
  try {
    const v = sessionStorage.getItem(key(ticketId));
    if (!v) return null;
    sessionStorage.removeItem(key(ticketId));
    return v;
  } catch {
    return null;
  }
}

/** Build the recap-email starter body. Plain HTML — pasted directly
 *  into the RichBodyEditor. The agent fills the bullets + edits as
 *  needed; their signature is appended by workflow 12 at send time. */
export function buildFollowupTemplate(args: {
  clientFirstName: string | null;
}): string {
  const greeting = args.clientFirstName
    ? `Hi ${escapeHtml(args.clientFirstName)},`
    : 'Hi,';
  return [
    `<p>${greeting}</p>`,
    `<p>Thanks for jumping on the live session just now — quick recap of what we covered:</p>`,
    `<ul><li><br></li><li><br></li></ul>`,
    `<p>If anything comes up or you have follow-up questions, just reply to this email.</p>`,
  ].join('');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
